import { useEffect, useState } from "react";

/**
 * Frente Bug B Layer 3 — VisualViewport keyboard offset (12/05/26), promovido
 * a FONTE ÚNICA das vars CSS de teclado no fix 66aa9c10 (08/26).
 *
 * iOS Safari ignores `interactive-widget=resizes-content` (the directive
 * we added in index.html for Layer 1) and instead rolls the FULL visual
 * viewport up when an input is focused — so a `position: fixed` drawer
 * follows the scroll and its content can disappear above the screen top.
 *
 * Solution: subscribe to `window.visualViewport` resize+scroll events and
 * derive the keyboard height in real time. Consumers (QuickTaskModal,
 * Notes Schedule modal) apply this as `paddingBottom` on their inner
 * scroll container so focused inputs stay visible above the keyboard.
 *
 * ── 66aa9c10: publicação das vars CSS mora AQUI, e só aqui ──────────────────
 * O MirrorChat mantinha uma cópia inline deste mesmo effect publicando
 * `--keyboard-offset`/`--vv-offset-top` — duas fontes do mesmo cálculo, e o
 * bug do painel coberto pelo teclado nasceu exatamente da conta feita em dois
 * lugares. Agora o consumidor que precisa das vars passa
 * `{ publishCssVars: true }` (UMA página por vez — hoje só o MirrorChat) e
 * este hook é o único lugar do repo que chama setProperty nessas vars
 * (pinado em src/test/mirrorKeyboardPanel.test.ts).
 *
 * Semânticas dos DOIS números (consumidores diferentes, contas diferentes):
 *   keyboardHeight (estado)  = innerHeight − vv.height − vv.offsetTop
 *     → padding para scroller INTERNO (QuickTaskModal/Notes). Desconta o
 *       offsetTop porque o conteúdo pan-ado já subiu essa distância.
 *   --keyboard-offset (var)  = innerHeight − vv.height − vv.offsetTop
 *     → inset entre o fundo do layout viewport e o fundo VISÍVEL
 *       (vv.offsetTop + vv.height). É o termo geométrico de um controle
 *       preso ao visual viewport; contar offsetTop de novo deixa a barra
 *       alta demais.
 *   --vv-offset-top (var)    = vv.offsetTop
 *     → counter-translate do palco do avatar (cancela o pan do iOS).
 *
 * ── Clamp de segurança (exigência 1 do fix, critério corrigido 29/08) ───────
 * O termo agora move DUAS superfícies (barra + painel). Uma medição quebrada
 * de visualViewport — o cenário real é Android SDK 35/36 + edge-to-edge
 * somando insets em dobro (capacitor#8432, capacitor-keyboard#28) — moveria
 * as duas para o meio da tela. O sample quebrado é DESCARTADO (→ 0), não
 * obedecido nem "limitado": obedecer um teclado que não existe é pior que
 * ignorar um que existe por um frame.
 *
 * O critério é um PISO DE ESPAÇO ÚTIL (vv.height mínimo), não fração da
 * tela. A primeira versão usava "teclado ≤ 60% da altura" — verdade em
 * retrato e FALSA em paisagem (iPhone deitado: viewport ~393px, teclado
 * ~250px = 64% → um teclado REAL era descartado e o bug original voltava,
 * intermitente e ligado à rotação; Info.plist permite landscape no iPhone e
 * as quatro orientações no iPad). O modo de falha do #8432 é a viewport
 * visível ficar quase sem nada — isso se detecta por vv.height PEQUENO, que
 * é indiferente à orientação.
 *
 * ── Fechamento de verdade (exigência 2 / quirk iOS 26) ──────────────────────
 * No iOS 26 o vv.offsetTop pode NÃO zerar depois que o teclado fecha (Apple
 * Dev Forums 800125). O sinal confiável de "fechou" é vv.height de volta ao
 * (quase) tamanho cheio — quando isso vale, TODAS as saídas zeram,
 * independentemente do offsetTop residual. Os caminhos de fechamento por
 * gesto (blur do campo) chama resetKeyboardCssVars() sem esperar
 * evento nenhum.
 *
 * Math worked example (iPhone 17 Pro Max):
 *   innerHeight=852, height=481, offsetTop=80
 *   keyboardHeight = 852 − 481 − 80 = 291 (≈ teclado iOS)
 *   --keyboard-offset = 852 − (481 + 80) = 291 (fundo visível)
 *
 * Android Chrome (layout viewport ENCOLHE com o teclado): innerHeight
 * acompanha vv.height → tudo aqui rende ~0 e o fluxo normal do layout cuida
 * do resto — o hook fica inerte por construção.
 *
 * Threshold `isKeyboardOpen = keyboardHeight > 100` excludes browser
 * chrome show/hide noise (Safari URL bar collapse is ≈ 60-90px).
 *
 * SSR-safe: `visualViewport` is undefined server-side; we return zeros
 * and register no listeners until the effect runs in the browser.
 */

export interface KeyboardOffset {
  /** Pixels of keyboard currently covering the bottom of the viewport. */
  keyboardHeight: number;
  /** True when keyboardHeight exceeds the 100px noise threshold. */
  isKeyboardOpen: boolean;
}

const KEYBOARD_OPEN_THRESHOLD_PX = 100;

/** ── Re-amostragem pós-foco (bug "a barra só sobe no 2º toque", 30/08) ──────
 *
 *  O iOS dispara o resize do visualViewport no INÍCIO da animação do
 *  teclado, com vv.height ainda (quase) cheio → raw ≤ epsilon → compute dá
 *  ZERO — e muitas vezes NÃO dispara um segundo resize ao fim da animação.
 *  O toque seguinte gera um "scroll" do vv, o compute roda de novo e aí
 *  acerta: é exatamente o sintoma do Rafa (barra atrás do teclado até o
 *  segundo toque). O problema é QUANDO se mede, não o que se descarta — o
 *  epsilon e o piso continuam certos e intocados.
 *
 *  Correção: focusin/focusout num campo editável disparam esta escada de
 *  re-medições. ESCADA FIXA, sem parada antecipada "dois samples iguais":
 *  no formato do próprio bug, dois samples cedo demais leem os DOIS o
 *  vv.height ainda cheio — iguais em zero — e a parada antecipada
 *  encerraria a escada antes de o teclado terminar de subir. O custo da
 *  escada completa é ~5 computes idempotentes (mesmo valor ⇒ mesma var);
 *  o custo da parada esperta é o bug de volta. rAF encadeado foi avaliado
 *  e rejeitado: durante a animação o vv atualiza em instantes irregulares
 *  (rAF amostraria dezenas de frames iguais), e timers escalonados cobrem
 *  a mesma janela de forma determinística e testável.
 *
 *  800ms de teto: a animação do teclado iOS leva ~250ms; o último degrau
 *  cobre folga para aparelhos lentos e para o quirk do offsetTop tardio. */
export const KEYBOARD_SETTLE_DELAYS_MS = [60, 160, 300, 500, 800] as const;

/** vv.height a até isto do cheio = teclado FECHADO (ruído de chrome ≈ 60-90px
 *  fica ACIMA do threshold de aberto, mas offsets pequenos não movem nada). */
export const KEYBOARD_CLOSED_EPSILON_PX = 24;

/** Piso de espaço útil: vv.height abaixo disto = medição quebrada
 *  (capacitor#8432 — insets somados em dobro deixam a viewport visível quase
 *  sem nada) e o sample inteiro é descartado. Indiferente à orientação, ao
 *  contrário do teto por fração que ele substitui (60% descartava um teclado
 *  REAL em paisagem: 250px de 393px = 64%).
 *
 *  120px porque precisa ficar ABAIXO da menor viewport visível legítima com
 *  teclado aberto — o pior caso real é iPhone SE em paisagem: 375px de
 *  altura − teclado compacto ~200px ≈ 175px visíveis — e ACIMA do que a
 *  medição quebrada produz (dupla contagem de insets deixa < ~100px). */
export const MIN_USABLE_VIEWPORT_PX = 120;

export interface KeyboardViewportSample {
  innerHeight: number;
  vvHeight: number;
  vvOffsetTop: number;
}

export interface KeyboardComputation {
  /** Padding p/ scroller interno (desconta offsetTop). */
  keyboardHeight: number;
  /** Inset até o fundo visível das superfícies fixed (--keyboard-offset). */
  fixedBarOffset: number;
  /** Counter-translate do palco (--vv-offset-top). */
  stageOffsetTop: number;
}

const ZERO: KeyboardComputation = { keyboardHeight: 0, fixedBarOffset: 0, stageOffsetTop: 0 };

// PURA e exportada: o clamp e a detecção de fechamento são comportamento de
// produção — o teste bate direto aqui com os números reais do diagnóstico.
export function computeKeyboardOffsets(s: KeyboardViewportSample): KeyboardComputation {
  const raw = s.innerHeight - s.vvHeight;
  // Fechado: vv.height (quase) cheio → zera TUDO, offsetTop residual incluso
  // (quirk iOS 26 — ver header).
  if (raw <= KEYBOARD_CLOSED_EPSILON_PX) return ZERO;
  // Quebrado: sobrou espaço útil de menos → descartado, não obedecido (ver
  // header — capacitor#8432; critério por piso, não por fração, para não
  // jogar fora teclado real em paisagem).
  if (s.vvHeight < MIN_USABLE_VIEWPORT_PX) return ZERO;

  const stageOffsetTop = Math.max(0, Math.round(s.vvOffsetTop));
  const pad = s.innerHeight - s.vvHeight - s.vvOffsetTop;
  return {
    keyboardHeight: pad > 0 ? Math.round(pad) : 0,
    fixedBarOffset: pad > 0 ? Math.round(pad) : 0,
    stageOffsetTop,
  };
}

function writeCssVars(c: KeyboardComputation): void {
  document.documentElement.style.setProperty("--keyboard-offset", c.fixedBarOffset + "px");
  document.documentElement.style.setProperty("--vv-offset-top", c.stageOffsetTop + "px");
}

/** Fechamento explícito por gesto (blur do campo):
 *  zera as vars JÁ, sem depender de evento do visualViewport — no iOS 26 o
 *  evento pós-fechamento pode chegar com offsetTop residual ou não chegar. */
export function resetKeyboardCssVars(): void {
  writeCssVars(ZERO);
}

export function useKeyboardOffset(options?: { publishCssVars?: boolean }): KeyboardOffset {
  const publish = options?.publishCssVars === true;
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;

    const compute = () => {
      const c = computeKeyboardOffsets({
        innerHeight: window.innerHeight,
        vvHeight: vv.height,
        vvOffsetTop: vv.offsetTop,
      });
      setKeyboardHeight(c.keyboardHeight);
      if (publish) writeCssVars(c);
    };

    // Escada de re-medição pós-foco (ver KEYBOARD_SETTLE_DELAYS_MS): o
    // resize do iOS chega cedo demais e o evento de fim de animação pode
    // nunca vir — depois de um focus/blur, medir de novo até estabilizar.
    let settleTimers: number[] = [];
    const cancelSettle = () => {
      for (const id of settleTimers) window.clearTimeout(id);
      settleTimers = [];
    };
    const scheduleSettle = () => {
      cancelSettle();
      settleTimers = KEYBOARD_SETTLE_DELAYS_MS.map((ms) => window.setTimeout(compute, ms));
    };
    const isEditableTarget = (t: EventTarget | null): boolean =>
      t instanceof Element && t.matches('input, textarea, [contenteditable="true"]');
    // focusin E focusout, pelo motivo simétrico: o fechamento do teclado tem
    // o mesmo evento prematuro (e o quirk iOS 26 de offsetTop residual).
    const onFocusChange = (e: FocusEvent) => {
      if (isEditableTarget(e.target)) scheduleSettle();
    };

    compute(); // seed initial value
    // offsetTop muda no scroll (não só no resize) — escuta os dois.
    vv.addEventListener("resize", compute);
    vv.addEventListener("scroll", compute);
    document.addEventListener("focusin", onFocusChange);
    document.addEventListener("focusout", onFocusChange);
    return () => {
      vv.removeEventListener("resize", compute);
      vv.removeEventListener("scroll", compute);
      document.removeEventListener("focusin", onFocusChange);
      document.removeEventListener("focusout", onFocusChange);
      cancelSettle();
      if (publish) {
        document.documentElement.style.removeProperty("--keyboard-offset");
        document.documentElement.style.removeProperty("--vv-offset-top");
      }
    };
  }, [publish]);

  return {
    keyboardHeight,
    isKeyboardOpen: keyboardHeight > KEYBOARD_OPEN_THRESHOLD_PX,
  };
}
